import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { AgentInterceptionType } from '../../reducers/messagingSlice';
import { AgentMode } from '../../redux/features/agentMode/agentMode.constants';
import {
  selectCanPromoteToOrder,
  selectShouldShowMyCart,
} from '../../redux/features/agentMode/agentMode.selector';
import { setAgentMode } from '../../redux/features/agentMode/agentMode.slice';
import { selectDraftCartItems } from '../../redux/features/draftCart/draftCart.selector';
import { replaceOrderWithDraftCart } from '../../redux/features/draftCart/draftCart.thunks';
import useAgentInterception from '../agentInterception/useAgentInterception.hooks';

/**
 * HITL-1615 / HITL-1645 — take over the order with My Cart.
 *
 * ## Why an interception has to go out, and why it goes FIRST
 *
 * C6's drafted AC assumed `agent_cart` would start flowing on its own "because
 * the mode is `Intercept`". Nothing downstream reads the mode. Three behaviours
 * read `isAIAutoMode`, and all three are still in their AI-driving state while
 * the agent sits in an ARMED `Intercept` — mode chosen, nothing emitted yet:
 *
 * - `useAgentCartSync.hooks.ts:35` returns early, so no `agent_cart` is
 *   published;
 * - `orderSlice.ts:115` returns early, so no `order` reaches the POS;
 * - `useHypothesisFrames.hooks.ts:41` keeps replaying, and its rebuild calls
 *   `clearCart()` (`:254`) — so the next changed frame DESTROYS the promoted
 *   cart.
 *
 * `isAIAutoMode` flips false only on a `sendAgentInterception`, and entering
 * `Intercept` deliberately emits nothing (HITL-1604's armed-versus-engaged
 * design: the choice stays reversible until something leaves the terminal).
 * Taking over is the moment it stops being reversible, so that is exactly where
 * the interception belongs.
 *
 * **Order matters.** `useHypothesisFrames` captures its arm baseline on the
 * true→false flip (`:110-116`). Emitting first means the baseline is the AI's
 * cart as it stands; the cart write that follows then reads as the agent's first
 * edit and disarms the intercept-import. Emitting after would bake the promoted
 * items into the baseline and leave the import armed.
 *
 * The emit cannot live in the thunk: it routes through `useAgentInterception`,
 * which is the HITL-1606 gate and is a hook. This hook owns the sequence so the
 * ordering exists in one place rather than at each call site.
 *
 * ## HITL-1645 — why the takeover spans two renders, and must
 *
 * The button is now offered in `Shadow`, where its first step is to enter
 * `Intercept`. That cannot be done in one handler. `useAgentInterception` reads
 * `selectIsAgentActionBlocked` through `useAppSelector`, so its gate holds the
 * value from the LAST RENDER: dispatching `setAgentMode` and then calling
 * `sendAgentInterception` in the same handler would hit a gate that still says
 * `Shadow`, and the interception would be refused — leaving the agent in
 * `Intercept` with the AI still driving and their cart unwritten.
 *
 * Rather than weaken the gate (it is the entire safety story, and `bypassModeGate`
 * exists only for system-originated interceptions), the mode change is dispatched
 * and the rest of the sequence is deferred to the render it causes. `canPromote`
 * turning true IS the confirmation that the store committed the mode, so the
 * effect below is both the continuation and the check — there is no path where
 * the cart is written while the gate would still refuse.
 *
 * The cost is one frame in which the console shows `Intercept` with My Cart
 * still full. The alternative costs an order.
 */
const usePromoteDraftCart = () => {
  const dispatch = useAppDispatch();
  const { sendAgentInterception } = useAgentInterception();
  const draftCartItems = useAppSelector(selectDraftCartItems);
  // The logic guard behind the control, same pattern as the rest of the feature.
  // Without it a refused interception would still be followed by the cart write —
  // the agent would silently move their draft into an order that cannot be sent,
  // and the AI's next frame would destroy it.
  const canPromote = useAppSelector(selectCanPromoteToOrder);
  // HITL-1645: the entry guard, mirroring the button's own gate. Both are the
  // "this column exists at all" predicate, which is already ANDed with
  // `hitl_shadow_mode` — so with the flag off this hook cannot change the mode,
  // let alone write the order, even if something called it directly.
  const canTakeOver = useAppSelector(selectShouldShowMyCart);

  const announce = (description: string) => {
    sendAgentInterception({
      data: {
        type: AgentInterceptionType.ORDER_CHANGED,
        description,
      },
    });
  };

  /**
   * Announce, then replace. Only ever called with `canPromote` true, so the
   * interception cannot be refused by the time the cart is written.
   */
  const announceAndReplace = () => {
    const count = Object.keys(draftCartItems ?? {}).length;
    if (!count) return;
    announce(`Order replaced with My Cart (${count} line(s))`);
    dispatch(replaceOrderWithDraftCart());
  };

  /**
   * Armed by a press made in `Shadow`; disarmed by the effect that completes it.
   *
   * A ref rather than state: it must not itself cause a render, and it is read
   * inside the effect the mode change schedules. It is cleared before the
   * sequence runs, so a mode change arriving from anywhere else — the switch,
   * a session reset — cannot replay a takeover.
   */
  const pendingTakeoverRef = useRef(false);

  useEffect(() => {
    if (!pendingTakeoverRef.current) return;
    // Still refused: the mode change did not land. Disarm rather than wait, so
    // an unrelated entry into `Intercept` later in the session cannot fire a
    // takeover the agent did not ask for.
    pendingTakeoverRef.current = false;
    if (!canPromote) return;
    announceAndReplace();
    // Deliberately keyed on `canPromote` alone. `announceAndReplace` closes over
    // this render's draft cart, which is the cart the agent pressed the button
    // on; adding it to the deps would re-run the sequence on an unrelated draft
    // edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPromote]);

  /**
   * HITL-1645 — the one way out of My Cart.
   *
   * Three things, in this order: enter `Intercept`, announce the interception,
   * write the cart. Already in `Intercept`, the first is a no-op and the rest
   * runs synchronously, exactly as `replaceOrder` did.
   *
   * Replace, not append, is what avoids the duplicate trap: an agent who
   * shadowed the whole order would otherwise double every line. It is also why
   * the per-line promote is gone — that one appended, and three lines copied in,
   * one fixed, all three sent back made a six-line order.
   *
   * No confirmation dialog. The press is irreversible — the AI is stood down for
   * the rest of the session — but a modal in the seconds before readback is
   * friction exactly where it costs most. The label carries the warning.
   */
  const takeOverWithMyCart = () => {
    if (!canTakeOver) return;
    if (!Object.keys(draftCartItems ?? {}).length) return;
    if (canPromote) {
      announceAndReplace();
      return;
    }
    pendingTakeoverRef.current = true;
    dispatch(setAgentMode(AgentMode.intercept));
  };

  return { takeOverWithMyCart };
};

export default usePromoteDraftCart;
